import { QUEUE_NAMES } from "../src/constants/queueName"
import type { Job } from "./api"

interface Stage {
    key: string
    label: string
}

// Every job runs this fixed detection sequence, in this exact order,
// regardless of what it finds — matches the real BullMQ queue chain in
// src/workers/index.ts.
const DETECTION_STAGES: Stage[] = [
    { key: QUEUE_NAMES.UPLOAD, label: "Downloading video" },
    { key: QUEUE_NAMES.METADATA, label: "Stripping metadata" },
    { key: QUEUE_NAMES.FRAME, label: "Extracting frames" },
    { key: QUEUE_NAMES.OCR, label: "Reading on-screen text" },
    { key: QUEUE_NAMES.REGEX, label: "Scanning for contact info" },
    { key: QUEUE_NAMES.QR, label: "Detecting QR codes" },
    { key: QUEUE_NAMES.SPEECH, label: "Transcribing speech" },
    { key: QUEUE_NAMES.FACE, label: "Detecting faces" },
    { key: QUEUE_NAMES.NUMBER_PLATE, label: "Detecting number plates" },
    { key: QUEUE_NAMES.LOGO, label: "Detecting logos" },
    { key: QUEUE_NAMES.DOCUMENT, label: "Detecting sensitive documents" },
    { key: QUEUE_NAMES.RISK, label: "Computing risk score" },
]

// Only runs if the risk score routes the job to PATCH — the risk worker
// decides this and sets job.status accordingly BEFORE any of these run, so
// job.status (not currentStage) is what tells us whether this tail applies.
const PATCH_STAGES: Stage[] = [
    { key: QUEUE_NAMES.PATCH, label: "Redacting video" },
    { key: QUEUE_NAMES.AUDIO_PATCH, label: "Redacting audio" },
    { key: QUEUE_NAMES.ENCODING, label: "Encoding final video" },
]

const PUBLISH_STAGE: Stage = { key: QUEUE_NAMES.PUBLISHING, label: "Publishing" }

type StepState = "done" | "current" | "pending" | "error"

interface StepperStep extends Stage {
    state: StepState
}

interface StepperPlan {
    steps: StepperStep[]
    terminal?: { label: string; tone: "warn" | "bad" }
}

function planSteps(job: Job): StepperPlan {
    const detectionKeys = DETECTION_STAGES.map((s) => s.key)
    const currentIndex = job.currentStage ? detectionKeys.indexOf(job.currentStage) : -1

    // Still within (or hasn't reached) the fixed detection+risk phase — the
    // eventual patch/approve path isn't decided yet.
    if (job.status === "PROCESSING" && currentIndex !== -1) {
        return {
            steps: DETECTION_STAGES.map((stage, i) => ({
                ...stage,
                state: i < currentIndex ? "done" : i === currentIndex ? "current" : "pending",
            })),
        }
    }

    if (job.status === "FAILED") {
        const failedAt = Math.max(currentIndex, 0)
        return {
            steps: DETECTION_STAGES.map((stage, i) => ({
                ...stage,
                state: i < failedAt ? "done" : i === failedAt ? "error" : "pending",
            })),
        }
    }

    if (job.status === "MANUAL_REVIEW" || job.status === "REJECTED") {
        const heaviest = job.riskBreakdown?.[0]
        const reason = heaviest ? ` — driven by ${heaviest.type} (+${heaviest.contribution})` : ""
        return {
            steps: DETECTION_STAGES.map((stage) => ({ ...stage, state: "done" })),
            terminal:
                job.status === "REJECTED"
                    ? { label: `Rejected — risk score too high to auto-publish${reason}`, tone: "bad" }
                    : { label: `Routed to manual review${reason}`, tone: "warn" },
        }
    }

    // Past risk assessment: patched vs. approved path is now decided.
    const tail = job.patched ? [...PATCH_STAGES, PUBLISH_STAGE] : [PUBLISH_STAGE]
    const tailKeys = tail.map((s) => s.key)
    const tailCurrentIndex = job.currentStage ? tailKeys.indexOf(job.currentStage) : -1
    const stillProcessing = job.status === "PROCESSING" || job.status === "PATCHED" || job.status === "APPROVED"

    return {
        steps: [
            ...DETECTION_STAGES.map((stage) => ({ ...stage, state: "done" as const })),
            ...tail.map((stage, i) => {
                if (!stillProcessing) return { ...stage, state: "done" as const } // COMPLETED
                if (i < tailCurrentIndex) return { ...stage, state: "done" as const }
                if (i === tailCurrentIndex) return { ...stage, state: "current" as const }
                return { ...stage, state: "pending" as const }
            }),
        ],
    }
}

function StepIcon({ state }: { state: StepState }) {
    if (state === "done") return <span className="step-icon step-icon-done">✓</span>
    if (state === "current") return <span className="step-icon step-icon-current" />
    if (state === "error") return <span className="step-icon step-icon-error">!</span>
    return <span className="step-icon step-icon-pending" />
}

export function PipelineStepper({ job }: { job: Job }) {
    const plan = planSteps(job)

    return (
        <div className="pipeline-stepper">
            {plan.steps.map((step, i) => (
                <div key={step.key} className={`step step-${step.state}`}>
                    <div className="step-marker">
                        <StepIcon state={step.state} />
                        {i < plan.steps.length - 1 && <span className="step-line" />}
                    </div>
                    <span className="step-label">{step.label}</span>
                </div>
            ))}
            {plan.terminal && (
                <div className={`terminal-banner terminal-${plan.terminal.tone}`}>{plan.terminal.label}</div>
            )}
        </div>
    )
}
